import React, { useState, useEffect, useContext, useCallback } from "react";
import styles from "./Dashboard.module.css";
import Map from "./Components/Map";
import MarkerPopup from "./Components/MarkerPopup";
import Calendar from "./Components/Marker/Calendar";
import FullCharts from "./Components/Marker/FullCharts";
import { divIcon } from "leaflet";
import ReactDOMServer from "react-dom/server";
import { DashboardOutorgaContext } from "../../../contexts/DashboardOutorga";
import mapMarker from "../../../assets/svg/map-marker.svg";
import { getStatusColor } from "../../../utils/outorga/dashboard";
import ReactLoading from "react-loading";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";

const Dashboard = () => {
  const context = useContext(DashboardOutorgaContext);
  const location = useLocation();
  const focusItemId = location.state?.focusItemId;

  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showFullCharts, setShowFullCharts] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(false);

  useEffect(() => {
    handleGetDashboard();
  }, []);

  useEffect(() => {
    if (!context.dashboard) return;

    const mappedLocations = context.dashboard.map((location) => ({
      id: location.id,
      posicao: location.posicao,
      descricao: location.nome,
      status: location.status,
      idMonitorado: location.monitorado.id,
      corMonitorado: location.monitorado.cor,
      tipoMonitorado: location.monitorado.tipo,
      icone: location.monitorado.icone,
      resumo: location?.condicoes_monitorado
        ?.filter((item) => item.exibir)
        .map(({ parametro_nome, valor_reporte, cor, nome }) => ({
          label: parametro_nome,
          value: valor_reporte,
          color: cor,
          condicaoNome: nome
        })),
      marker: {
        outorga: location.condicoes_ativas?.map((condicao) => ({
          label: condicao.nome,
          value: condicao.valor_reporte,
          limit: condicao.condicao?.excedido?.fim ?? 0,
          unit: "",
          status: condicao.status,
          percentage: condicao.porcentagem
        })),
        graficos: location.graficos?.map((grafico) => ({
          id: grafico.id,
          descricao: grafico.parametro_principal.nome,
          datas: grafico.horas?.map((hora) => `${String(hora).padStart(2, "0")}:00`),
          series: [
            {
              name: grafico.parametro_principal.nome,
              data: grafico.valores ?? [],
              tipo: grafico.parametro_principal.tipo_grafico === "linha" ? "line" : "bar"
            },
            ...grafico.parametros_mesclados.map((parametro) => ({
              name: parametro.nome,
              data: parametro.valores ?? [],
              tipo: parametro.tipo_grafico === "linha" ? "line" : "bar"
            }))
          ]
        }))
      }
    }));

    setLocations(mappedLocations);
  }, [context.dashboard]);

  const handleGetDashboard = () => {
    context.getGrantDashboard().then((res) => {
      if (res.status == 200) {
        setLastUpdate(new Date().toLocaleString());
      }
    });
  };

  const generateMarkers = useCallback(() => {
    return locations.map((location) => ({
      id: location.id,
      position: location.posicao,
      title: location.descricao,
      status: location.status,
      info: location.resumo,
      tipoMonitorado: location.tipoMonitorado,
      icon: divIcon({
        className: "default-div-icon",
        iconSize: [32, 32],
        iconAnchor: [16, 28],
        html: ReactDOMServer.renderToString(
          <div
            className="default-marker-container"
            style={{ backgroundColor: location.corMonitorado }}
          >
            <div
              className="default-marker-status"
              style={{ backgroundColor: getStatusColor(location.status) }}
            />

            {location.icone ? (
              <i className={location.icone + " la-2x"}></i>
            ) : (
              <img src={mapMarker} width={42} height={42} />
            )}

            <div
              className="default-marker-gradient"
              style={{ backgroundColor: getStatusColor(location.status) }}
            />
            <div
              className="default-marker-triangle"
              style={{ backgroundColor: location.corMonitorado }}
            />
          </div>
        )
      }),
      popupBody: () => (
        <MarkerPopup
          key={`${location.id}-${selectedLocationId}`}
          title={location.descricao}
          status={location.status}
          grantId={location.id}
          idMonitorado={location.idMonitorado}
          marker={location.marker}
          isActive={selectedLocationId === location.id}
          openCalendar={() => setShowCalendar(true)}
          openFullCharts={() => setShowFullCharts(true)}
        />
      )
    }));
  }, [locations, selectedLocationId]);

  return (
    <div className={styles.container}>
      {context.isLoadingDashboard &&
        <div className={styles.loadingDashboard}>
          <ReactLoading
            type="cylon"
            color="var(--background-menu)"
            width={40}
          />
        </div>
      }
      <div className="hdv-default-header mx-3">
        <span className="screen-menu-desc">
          <h4>Meus locais</h4>
          <div className="gray-background">
            <i className="fa fa-map-marker-alt fa-25px" />
          </div>
        </span>
        <div className="d-flex align-items-center gap-3">
          <label className={styles.headerLastUpdate}>
            Última atualização da página: {lastUpdate}
          </label>
          <button
            className="hdv-popover-button"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "36px",
              width: "36px"
            }}
            onClick={() => handleGetDashboard()}
            disabled={context.isLoadingDashboard}
          >
            <i className="las la-redo-alt la-2x"></i>
          </button>
        </div>
      </div>

      <Map
        locations={generateMarkers()}
        selectedLocationId={selectedLocationId}
        filters={context.filters}
        handlePopupOpen={setSelectedLocationId}
        handlePopupClose={(id) => setSelectedLocationId((prev) => prev === id ? null : prev)}
        focusItemId={focusItemId}
      />

      <Calendar
        grantId={selectedLocationId}
        showCalendar={showCalendar}
        closeCalendar={() => setShowCalendar(false)}
      />

      <FullCharts
        grantId={selectedLocationId}
        showFullCharts={showFullCharts}
        closeFullCharts={() => setShowFullCharts(false)}
      />
    </div>
  );
}

export default Dashboard;